import { memo, useEffect, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner
} from "reactstrap";
import {
    OPEN_ITEMS_MODULE_FILTER_ALL,
    buildRecommendedActionModuleFilterOptions,
    filterRecommendedActionItems,
    groupRecommendedActionItems,
    paginateRecommendedActionItems
} from "../../helpers/nextStepsHelpers";
import RecommendedActionItem from "../recommended-action-item";

import "./style.scss";

const OpenItemsModal = ({
    t,
    assetId,
    isOpen,
    toggle,
    itemsQuery
}) => {
    const items = itemsQuery?.data?.data?.items || [];
    const summary = itemsQuery?.data?.data?.summary || {};

    const [moduleFilter, setModuleFilter] = useState(OPEN_ITEMS_MODULE_FILTER_ALL);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [collapsedGroups, setCollapsedGroups] = useState({});

    useEffect(() => {
        if (!isOpen) {
            setModuleFilter(OPEN_ITEMS_MODULE_FILTER_ALL);
            setSearchQuery("");
            setPage(1);
            setCollapsedGroups({});
        }
    }, [isOpen]);

    const moduleFilterOptions = useMemo(() => (
        buildRecommendedActionModuleFilterOptions(items, t)
    ), [items, t]);

    const filteredItems = useMemo(() => (
        filterRecommendedActionItems(items, {
            moduleFilter,
            searchQuery
        }, t)
    ), [
        items,
        moduleFilter,
        searchQuery,
        t
    ]);

    const groupedItems = useMemo(() => (
        groupRecommendedActionItems(filteredItems, t)
    ), [filteredItems, t]);

    const orderedItems = useMemo(
        () => groupedItems.flatMap((group) => group.items),
        [groupedItems]
    );

    const pagination = useMemo(
        () => paginateRecommendedActionItems(orderedItems, page),
        [orderedItems, page]
    );

    useEffect(() => {
        if (page !== pagination.page) {
            setPage(pagination.page);
        }
    }, [page, pagination.page]);

    const groups = useMemo(() => {
        const visibleIds = new Set(pagination.items.map((item) => item.id));

        return groupedItems
            .map((group) => ({
                ...group,
                total: group.items.length,
                items: group.items.filter((item) => visibleIds.has(item.id))
            }))
            .filter((group) => group.items.length > 0);
    }, [groupedItems, pagination.items]);

    const totalCount = Number.isFinite(summary.total) ? summary.total : items.length;
    const hasActiveFilters = moduleFilter !== OPEN_ITEMS_MODULE_FILTER_ALL
        || searchQuery.trim() !== "";

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            scrollable
            className="overview-open-items"
        >
            <ModalHeader toggle={toggle}>
                <span className="d-inline-flex align-items-baseline gap-2">
                    <span>{t("All open items")}</span>
                    {totalCount > 0 && (
                        <span className="text-muted small fw-normal">
                            ({totalCount})
                        </span>
                    )}
                </span>
            </ModalHeader>

            <ModalBody>
                {itemsQuery.isFetching && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                    </div>
                )}

                {!itemsQuery.isFetching && itemsQuery.isError && (
                    <div className="text-center py-4 text-danger">
                        {t("An error occurred while fetching open items.")}
                    </div>
                )}

                {!itemsQuery.isFetching && !itemsQuery.isError && (
                    <>
                        <p className="small text-muted mb-3">
                            {t("All assessment gaps that require your attention")}
                        </p>

                        <Input
                            id="overview-open-items-search"
                            className="mb-3"
                            type="search"
                            value={searchQuery}
                            onChange={(event) => {
                                setSearchQuery(event.target.value);
                                setPage(1);
                            }}
                            placeholder={t("Search by title, description, or module")}
                        />

                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {moduleFilterOptions.map((option) => {
                                const label = option.key === OPEN_ITEMS_MODULE_FILTER_ALL
                                    ? t("All modules")
                                    : option.label;

                                return (
                                    <Button
                                        key={option.key}
                                        size="sm"
                                        color={moduleFilter === option.key ? "primary" : "light"}
                                        onClick={() => {
                                            setModuleFilter(option.key);
                                            setPage(1);
                                        }}
                                    >
                                        {label} ({option.count})
                                    </Button>
                                );
                            })}
                        </div>

                        {hasActiveFilters && (
                            <div className="small text-muted mb-3">
                                {t("Showing {{shown}} of {{total}} filtered items", {
                                    shown: filteredItems.length,
                                    total: items.length
                                })}
                            </div>
                        )}

                        {groups.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                {hasActiveFilters
                                    ? t("No items match your filters.")
                                    : t("No next steps available.")}
                            </div>
                        ) : groups.map((group) => {
                            const isCollapsed = Boolean(collapsedGroups[group.key]);

                            return (
                                <section key={group.key} className="overview-open-items__group">
                                    <button
                                        type="button"
                                        className="overview-open-items__group-header"
                                        onClick={() => {
                                            setCollapsedGroups((current) => ({
                                                ...current,
                                                [group.key]: !current[group.key]
                                            }));
                                        }}
                                    >
                                        <span>{group.label}</span>
                                        <span className="overview-open-items__group-count">
                                            {t("{{count}} items", { count: group.total })}
                                            {isCollapsed ? <HiChevronDown /> : <HiChevronUp />}
                                        </span>
                                    </button>
                                    {!isCollapsed && (
                                        <div className="overview-open-items__group-body">
                                            {group.items.map((row) => (
                                                <RecommendedActionItem
                                                    key={row.id}
                                                    row={row}
                                                    assetId={assetId}
                                                    t={t}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </section>
                            );
                        })}

                        {pagination.pageCount > 1 && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                <span className="small text-muted">
                                    {t("Showing {{start}}–{{end}} of {{total}}", {
                                        start: pagination.start,
                                        end: pagination.end,
                                        total: pagination.total
                                    })}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        size="sm"
                                        color="light"
                                        disabled={pagination.page <= 1}
                                        onClick={() => setPage((current) => current - 1)}
                                    >
                                        {t("Previous")}
                                    </Button>
                                    <span className="small text-muted">
                                        {t("Page")} {pagination.page} / {pagination.pageCount}
                                    </span>
                                    <Button
                                        size="sm"
                                        color="light"
                                        disabled={pagination.page >= pagination.pageCount}
                                        onClick={() => setPage((current) => current + 1)}
                                    >
                                        {t("Next")}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default withNamespaces()(memo(OpenItemsModal));
